// src/ui/bulkRun/BulkRunContext.js
//
// App-level bulk-run provider. Owns ONE in-flight bulk action (send or
// download) as React state, mounted ABOVE the tab-keyed shell content (see
// App.js) so the run survives in-app navigation. The segment that starts a run
// hands off a "run descriptor" at Confirm; from then on the provider drives
// execution + progress and BulkRunDock renders the modal/pill. Tab switches
// remount the segment but NOT the provider, so the run no longer dies.
//
// Scope: client-only "follow me across tabs" (L2). A full reload / sign-out
// still ends the run — there is no server-side job queue.

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { isSuccessStatus, isSkippedStatus } from '../../utils/billing/bulkRowStatus';

const BulkRunContext = createContext(null);

/** Count rows by terminal status. `unconfirmed` = the write may have landed
 *  (never retryable, never shown as an error). `stopped` = rows that never
 *  reached a terminal state (only meaningful once run.stopped is true after
 *  an early Stop) — excludes unconfirmed, which IS terminal. */
function deriveCounts(items) {
  let success = 0;
  let failure = 0;
  let skipped = 0;
  let unconfirmed = 0;
  for (const it of items) {
    // KAN-1028: already_sent counts as sent; in_flight / sent_recently as skipped.
    // `limited` stays out of every bucket, so it reads as "stopped" (not reached).
    if (isSuccessStatus(it.status)) success += 1;
    else if (it.status === 'failure') failure += 1;
    else if (isSkippedStatus(it.status)) skipped += 1;
    else if (it.status === 'unconfirmed') unconfirmed += 1;
  }
  return {
    success,
    failure,
    skipped,
    unconfirmed,
    stopped: items.length - (success + failure + skipped + unconfirmed),
  };
}

export function BulkRunProvider({ children, signedIn }) {
  // The single run slot. null = idle. Shape (RunView):
  //   { kind, tag, copy, Icon, backgroundable, note, phase, expanded,
  //     seamlessEnter, items, counts, stopped, stoppable }
  // tag/copy/Icon/backgroundable/note are kind:'action' only (undefined for
  // send/download/download-estimates) — see WAVE5_CONTRACT.md's locked engine API.
  const [run, setRun] = useState(null);
  // Mutable mirrors so async callbacks read fresh values without re-subscribing.
  const runRef = useRef(null);
  runRef.current = run;
  const descriptorRef = useRef(null);
  // KAN-1028: each PASS owns its own cancel token ({ current }). Stop / sign-out cancel
  // the CURRENT token; a new pass makes a fresh one, so a pass from before sign-out
  // (or an earlier run) can never be un-cancelled by a later start() / retry.
  const cancelTokenRef = useRef({ current: false });
  // The single slot is CLAIMED synchronously in start() (state lags a render), so
  // two same-tick starts can never both pass. Freed wherever the run clears.
  const slotRef = useRef(false);
  // Which run / which sign-in a pass belongs to: a late pass from a dismissed
  // or signed-out run must never write into (or toast over) the next one.
  const runTokenRef = useRef(0);
  const sessionGenRef = useRef(0);
  // Stop EVENT — fires in BOTH halt paths (the complete-phase stop AND the
  // download "cleared, no summary" stop) so the dock can show a stop toast for
  // EVERY kind. A run-state flag can't carry the download case: that path sets
  // run=null, so the dock has nothing to read. The nonce makes it fire once.
  const [stoppedEvent, setStoppedEvent] = useState(null);
  const stopNonceRef = useRef(0);
  const fireStopped = useCallback((gen) => {
    // A stop that finishes after sign-out belongs to the OLD session — no toast.
    if (gen !== undefined && gen !== sessionGenRef.current) return;
    // kind/copyVariant come from descriptorRef (set synchronously in start, so
    // always reliable regardless of render timing); counts from the live run if
    // present (download clears it, so fall back to zeros).
    const d = descriptorRef.current;
    if (!d) return;
    const r = runRef.current;
    stopNonceRef.current += 1;
    setStoppedEvent({
      kind: d.kind,
      copyVariant: d.copyVariant,
      copy: d.copy,
      meta: d.meta,
      counts: r?.counts ?? {
        success: 0, failure: 0, skipped: 0, unconfirmed: 0, stopped: 0,
      },
      nonce: stopNonceRef.current,
    });
  }, []);

  // Run one pass over `passItems`, mapping each local index back to the real row
  // index in run.items. Used by the initial pass (all rows) and retry (failed rows).
  const executePass = useCallback(async (descriptor, indices, passItems) => {
    const token = runTokenRef.current;
    const gen = sessionGenRef.current;
    const isCurrent = () => runTokenRef.current === token;
    const canceledRef = { current: false };
    cancelTokenRef.current = canceledRef;
    const ctx = {
      items: passItems,
      canceledRef,
      // KAN-711: a multi-step descriptor (kind 'collect') names the step it is on
      // ('creating' | 'sending' | 'waiting') so the pill can say so.
      setStep: (step) => {
        if (!isCurrent()) return;
        setRun((r) => (r ? { ...r, step } : r));
      },
      onProgress: (localIdx, update) => {
        if (!isCurrent()) return;
        const realIdx = indices[localIdx];
        setRun((r) => {
          if (!r) return r;
          const items = [...r.items];
          items[realIdx] = { ...items[realIdx], ...update };
          return { ...r, items, counts: deriveCounts(items) };
        });
      },
    };

    let result;
    try {
      result = await descriptor.execute(ctx);
    } catch (err) {
      result = { canceled: false, showSummary: true, error: err };
    }
    result = result || {};
    if (!isCurrent()) return; // dismissed / signed out while this pass was still finishing

    // A canceled pass that asked NOT to show a summary (download: nothing zipped)
    // clears the slot entirely — no pill, no modal. Still announce the stop:
    // the user explicitly stopped it, so the stop toast fires via the event
    // before the run is cleared (the dock renders the toast even when run=null).
    if (result.canceled && result.showSummary === false) {
      fireStopped(gen);
      slotRef.current = false;
      setRun(null);
      return;
    }

    // A run can be halted early under two vocabularies: download returns
    // `canceled` (pure compute, nothing zipped), send returns `stopped` (loop
    // broke after some sends went out). Treat either as "stopped" so run.stopped
    // is truthful for BOTH kinds.
    const halted = !!result.canceled || !!result.stopped;
    // result.note (mode="action" descriptors) REPLACES run.note each pass —
    // initial and every retry — never merged/appended.
    setRun((r) => (r ? {
      ...r, phase: 'complete', stopping: false, stopped: halted || r.stopped, note: result.note,
      rateLimit: result.rateLimited ? { minutes: result.rateLimitMinutes ?? null } : null,
    } : r));
    if (halted) fireStopped(gen);
    setRun((r) => (r && r.step ? { ...r, step: undefined } : r));
    try {
      await descriptor.onComplete?.(result);
    } catch (_) { /* fail-soft: completion side effects never block the summary */ }
  }, [fireStopped]);

  const start = useCallback((descriptor) => {
    // Concurrency guard: one run at a time. start() refuses (false) while a run is
    // active OR a finished run's result hasn't been seen/dismissed yet — callers
    // keep their review open and show the "finish the previous one" toast.
    // Claimed synchronously so two same-tick starts can't both pass.
    if (slotRef.current) return false;
    slotRef.current = true;
    runTokenRef.current += 1;
    descriptorRef.current = descriptor;

    const { items } = descriptor;
    setRun({
      kind: descriptor.kind,
      // tag: a caller-defined sub-kind for kind:'action' ('lifecycle-status' |
      // 'route-transfer' | 'dedup') — informational, not read by the engine.
      tag: descriptor.tag,
      // copy/Icon/backgroundable/note: kind:'action' only (see
      // BulkActionProgressModal mode="action" + WAVE5_CONTRACT.md). Always
      // present on `run` (undefined for other kinds) so the Dock can pass
      // them through unconditionally.
      copy: descriptor.copy,
      Icon: descriptor.Icon,
      backgroundable: descriptor.backgroundable !== false,
      note: undefined,
      // copyVariant: 'send' (default) | 'reminder'. Flows to BulkRunDock →
      // BulkSendProgressModal for per-kind titles / footer copy.
      copyVariant: descriptor.copyVariant || 'send',
      // kind 'collect' (KAN-711): descriptor.meta ({ mode }) is read by labels; `step` is set by the pass.
      meta: descriptor.meta,
      step: undefined,
      phase: 'running',
      expanded: true,
      // Existing kinds always seamless-mount (the review→running handoff);
      // kind:'action' hands off from a DIFFERENT modal (the caller's own
      // wizard), so it plays its own enter by passing seamlessEnter: false.
      seamlessEnter: descriptor.seamlessEnter ?? true,
      items,
      counts: deriveCounts(items),
      stopped: false,
      stopping: false,
      stoppable: descriptor.stoppable !== false,
    });

    executePass(descriptor, items.map((_, i) => i), items);
    return true;
  }, [executePass]);

  // Stop is honoured BETWEEN rows (the current row is bounded by the step time
  // limits); `stopping` shows while that row finishes.
  const stop = useCallback(() => {
    cancelTokenRef.current.current = true;
    setRun((r) => (r && r.phase === 'running' ? { ...r, stopping: true } : r));
  }, []);
  const collapse = useCallback(() => {
    setRun((r) => (r ? { ...r, expanded: false } : r));
  }, []);
  const expand = useCallback(() => {
    setRun((r) => (r ? { ...r, expanded: true } : r));
  }, []);
  const dismiss = useCallback(() => {
    try { descriptorRef.current?.onDismiss?.(); } catch (_) { /* best effort */ }
    slotRef.current = false;
    runTokenRef.current += 1;
    setRun(null);
  }, []);

  const retryFailures = useCallback(() => {
    const r = runRef.current;
    const descriptor = descriptorRef.current;
    if (!r || !descriptor || r.phase !== 'complete') return;
    // noRetry (kind:'action' descriptors) marks a failure the caller already
    // knows can't be re-sent (e.g. a validation error, not a transient one) —
    // skip it so Retry only re-runs rows that can actually succeed differently.
    // Always undefined for download/send, so this is the same set as before.
    const failedIndices = r.items
      .map((it, i) => (it.status === 'failure' && !it.noRetry ? i : -1))
      .filter((i) => i >= 0);
    if (failedIndices.length === 0) return;
    // Re-run the ORIGINAL descriptor items (clean work units), not run.items —
    // those carry the status/message merged in by the first pass's onProgress,
    // which would leak a stale 'failure' status into the retry's ctx.items.
    const passItems = failedIndices.map((i) => descriptor.items[i]);
    setRun((prev) => {
      if (!prev) return prev;
      return {
        ...prev, phase: 'running', stopped: false, stopping: false,
      };
    });
    executePass(descriptor, failedIndices, passItems);
  }, [executePass]);

  // Security: the run holds tenant data (customer names, invoice numbers,
  // amounts). Its lifetime is tied to the authenticated session — when the
  // session ends (logout, idle timeout, token expiry, cross-tab sign-out) the
  // App flips `signedIn` to false; clear the run so its modal can't paint over
  // the login screen, and can't resurrect if the user signs back in on the same
  // tab (no reload). `signedIn` is undefined in isolated provider tests, so the
  // `=== false` guard leaves them untouched.
  useEffect(() => {
    if (signedIn !== false) return undefined;
    cancelTokenRef.current.current = true; // halt any in-flight render/zip pass
    sessionGenRef.current += 1; // a stop finishing later must not toast into the next sign-in
    runTokenRef.current += 1;
    try { descriptorRef.current?.onDismiss?.(); } catch (_) { /* best effort */ }
    slotRef.current = false;
    setRun(null);
    setStoppedEvent(null); // don't fire a stop toast onto the login screen
    return undefined;
  }, [signedIn]);

  // A run in flight must not die silently on reload / tab close: standard
  // browser prompt while running, removed the moment it is idle.
  const isRunningNow = !!run && run.phase === 'running';
  useEffect(() => {
    if (!isRunningNow || typeof window === 'undefined') return undefined;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // required by Chrome / Safari to show the prompt
      return '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isRunningNow]);

  // Clear seamlessEnter after the first commit so a later re-expand animates
  // normally (the seamless mount is only for the review→running handoff).
  useEffect(() => {
    if (!run?.seamlessEnter) return undefined;
    const id = setTimeout(() => {
      setRun((r) => (r && r.seamlessEnter ? { ...r, seamlessEnter: false } : r));
    }, 0);
    return () => clearTimeout(id);
  }, [run?.seamlessEnter]);

  const value = useMemo(() => ({
    run,
    isRunning: !!run && run.phase === 'running',
    stopping: !!run?.stopping,
    stoppedEvent,
    start,
    stop,
    collapse,
    expand,
    dismiss,
    retryFailures,
  }), [run, stoppedEvent, start, stop, collapse, expand, dismiss, retryFailures]);

  return <BulkRunContext.Provider value={value}>{children}</BulkRunContext.Provider>;
}

BulkRunProvider.propTypes = { children: PropTypes.node, signedIn: PropTypes.bool };

/** Is a bulk run active? False (never throws) outside a BulkRunProvider, so greeting gates stay testable. */
export function useIsBulkRunActive() {
  const ctx = useContext(BulkRunContext);
  return !!ctx?.isRunning;
}

export function useBulkRun() {
  const ctx = useContext(BulkRunContext);
  if (!ctx) throw new Error('useBulkRun must be used within a BulkRunProvider');
  return ctx;
}
